
import { supabase } from "@/integrations/supabase/client";
import { Reagent } from "@/types/reagent.types";
import { getUserEmail } from "./getUserEmail";
import { shouldNotifyUser } from "./notificationPreferences";
import { getSwapRequestUrl, sendEmail } from "./emailUtils";
import { sendPushNotification } from "./oneSignalService";

// Send a notification about a new swap offer
export const sendSwapOfferNotification = async (
  swapRequest: { id: string; reagent_id: string; requester_id: string },
  reagent: Reagent,
  recipientId: string
): Promise<boolean> => {
  try {
    // Check if the user wants to receive swap offer notifications
    const shouldNotify = await shouldNotifyUser(recipientId, "swap_offers");
    if (!shouldNotify) {
      console.log("User has disabled swap offer notifications");
      return false;
    }
    
    // Get the recipient's email
    const recipientEmail = await getUserEmail(recipientId);
    if (!recipientEmail) {
      console.error("Could not find recipient's email");
      return false;
    }
    
    // Get the requester's username for the email
    const { data: requesterProfile } = await supabase
      .from("profiles")
      .select("username, lab, university")
      .eq("id", swapRequest.requester_id)
      .single();
      
    const requesterName = requesterProfile?.username || "A user";
    
    // Compose the email
    const subject = `LabSwApp - You have received a swap offer for ${reagent.name}`;
    const swapUrl = getSwapRequestUrl(swapRequest.id);
    
    const html = `
      <h2>You've received a swap offer!</h2>
      <p>${requesterName} has requested to swap for your reagent: <strong>${reagent.name}</strong>.</p>
      <p>Click the button below to review this swap request:</p>
      <a href="${swapUrl}" style="display: inline-block; background-color: #4F46E5; color: white; padding: 10px 15px; text-decoration: none; border-radius: 4px; margin-top: 15px;">View Swap Request</a>
      <p style="margin-top: 20px; color: #666;">If the button doesn't work, copy and paste this link in your browser: ${swapUrl}</p>
    `;
    
    console.log(`Sending swap offer notification to ${recipientEmail} for reagent ${reagent.name}`);
    
    // Send the email
    const emailSuccess = await sendEmail(recipientEmail, subject, html);
    
    // Also send push notification
    try {
      await sendPushNotification(
        [recipientId],
        'New Swap Request',
        `${requesterName} wants to swap for your reagent: ${reagent.name}`,
        {
          type: 'swap_offer',
          swapRequestId: swapRequest.id,
          reagentId: reagent.id,
          requesterId: swapRequest.requester_id,
          url: `/swap-request/${swapRequest.id}`
        }
      );
    } catch (pushError) {
      console.error('Failed to send push notification:', pushError);
      // Don't fail the whole function if push notification fails
    }
    
    return emailSuccess;
  } catch (error) {
    console.error("Exception in sendSwapOfferNotification:", error);
    return false;
  }
};
